import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

interface MemberWithDevices {
  memberId: string;
  memberName: string;
  userId: string;
  hasActiveDevices: boolean;
}

const ACTIVE_DEVICE_DAYS = 30;

export const useAvailableCallMembers = (familyId: string | null) => {
  const [members, setMembers] = useState<MemberWithDevices[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!familyId) {
      setMembers([]);
      return;
    }

    const fetchMembers = async () => {
      setLoading(true);
      try {
        const membersSnap = await getDocs(
          query(collection(db, 'members'), where('familyId', '==', familyId))
        );

        const membersWithUserId = membersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter((m: any) => m.userId);

        const devicesSnap = await getDocs(
          query(collection(db, 'devices'), where('familyId', '==', familyId))
        );

        const now = Date.now();
        const cutoff = now - ACTIVE_DEVICE_DAYS * 24 * 60 * 60 * 1000;

        const activeDevicesByMember = new Set<string>();
        devicesSnap.docs.forEach(d => {
          const data = d.data();
          const lastSeenAt = data.lastSeenAt;
          const lastSeenMs = lastSeenAt instanceof Timestamp 
            ? lastSeenAt.toMillis() 
            : typeof lastSeenAt === 'number' 
            ? lastSeenAt 
            : 0;

          if (lastSeenMs >= cutoff && data.memberId) {
            activeDevicesByMember.add(data.memberId);
          }
        });

        const result: MemberWithDevices[] = membersWithUserId.map((m: any) => ({
          memberId: m.id,
          memberName: m.name || m.displayName || 'Sin nombre',
          userId: m.userId,
          hasActiveDevices: activeDevicesByMember.has(m.id),
        }));

        setMembers(result);
      } catch (error) {
        console.error('Error fetching available call members:', error);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [familyId]);

  return { members, loading };
};
